import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Button, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { HEALTH_STATUS_COLORS, VISIT_COLORS, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";
import TableSearchBar from "./TableSearchBar";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

const LABS_OPTIONS = ["All", "With Labs", "Without Labs"];
const PATIENT_STATUS_OPTIONS = ["All", "Active", "Contacted", "Scheduled", "Reconciled", "Pending Review"];

const PATIENT_STATUS_COLORS = {
  "Active":         { bg: "#DBEAFE", text: "#1D4ED8" },
  "Contacted":      { bg: "#E0E7FF", text: "#4338CA" },
  "Scheduled":      { bg: "#FEF3C7", text: "#92400E" },
  "Reconciled":     { bg: "#DCFCE7", text: "#166534" },
  "Pending Review": { bg: "#FEE2E2", text: "#991B1B" },
};

/**
 * Tabla de pacientes con polifarmacia — con estatus de paciente.
 *
 * @param {{
 *   patients: Array,
 *   onCreateTask: (row) => void
 * }} props
 */
export default function PolypharmacyTable({ patients, onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [search, setSearch] = useState("");
  const [labsFilter, setLabsFilter] = useState("All");
  const [patientStatusFilter, setPatientStatusFilter] = useState("All");

  const filteredRows = useMemo(() => {
    if (!patients) return [];
    const lowerSearch = search.toLowerCase();
    return patients.filter((row) => {
      if (lowerSearch && !row.name?.toLowerCase().includes(lowerSearch)) return false;
      if (labsFilter === "With Labs" && row.recentLabs !== "Y") return false;
      if (labsFilter === "Without Labs" && row.recentLabs !== "N") return false;
      if (patientStatusFilter !== "All" && row.patientStatus !== patientStatusFilter) return false;
      return true;
    });
  }, [patients, search, labsFilter, patientStatusFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Patient",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "patientStatus",
      headerName: "Patient Status",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const colors = PATIENT_STATUS_COLORS[value] || { bg: "#F1F5F9", text: "#64748B" };
        return (
          <Chip
            label={value || "—"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.68rem",
              bgcolor: colors.bg,
              color: colors.text,
              height: 22,
            }}
          />
        );
      },
    },
    {
      field: "medications",
      headerName: "Meds",
      width: 75,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return "—";

        let bgcolor = "transparent";
        let color = "text.primary";

        if (val >= 8) {
          bgcolor = "#FEE2E2";
          color = "#DC2626";
        } else if (val >= 5) {
          bgcolor = "#FEF3C7";
          color = "#D97706";
        }

        return (
          <Chip
            label={val}
            size="small"
            sx={{
              bgcolor,
              color,
              fontWeight: 700,
              height: 22,
              fontSize: "0.75rem",
            }}
          />
        );
      },
    },
    {
      field: "healthStatus",
      headerName: "Health Status",
      width: 110,
      renderCell: (params) => {
        const val = params.value;
        const palette = HEALTH_STATUS_COLORS[val] || HEALTH_STATUS_COLORS["No Status"];
        return (
          <Chip
            label={val || "N/A"}
            size="small"
            sx={{
              bgcolor: palette.bg,
              color: palette.text,
              fontWeight: 600,
              height: 22,
              fontSize: "0.72rem",
            }}
          />
        );
      },
    },
    {
      field: "conditions",
      headerName: "Conditions",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            maxWidth: "100%",
          }}
          title={params.value}
        >
          {params.value || "—"}
        </Box>
      ),
    },
    {
      field: "recentLabs",
      headerName: "Labs",
      width: 65,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value === "Y") {
          return <CheckCircleIcon sx={{ fontSize: 18, color: "#16A34A" }} />;
        }
        return <WarningAmberIcon sx={{ fontSize: 18, color: "#D97706" }} />;
      },
    },
    {
      field: "lastVisit",
      headerName: "Last Visit",
      width: 100,
      renderCell: (params) => {
        const val = params.value;
        const palette = VISIT_COLORS[val];
        if (!palette) return val || "—";
        return (
          <Chip
            label={val}
            size="small"
            sx={{
              bgcolor: palette.bg,
              color: palette.text,
              fontWeight: 600,
              height: 22,
              fontSize: "0.72rem",
            }}
          />
        );
      },
    },
    {
      field: "globalIndicator",
      headerName: "Global",
      width: 75,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lastSantePlusVisit",
      headerName: "SP Visit",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const value = params.value;
        const age = getSpVisitAge(value);
        const colors = SP_VISIT_COLORS[age];
        if (!value) {
          return <Chip label="—" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        }
        const d = new Date(value);
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
      },
    },
    {
      field: "observationsCount",
      headerName: "Obs",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const val = params.value || 0;
        if (val === 0) return "0";
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
      },
    },
    {
      field: "activeTasks",
      headerName: "Tasks",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const val = params.value || 0;
        if (val === 0) return "0";
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
      },
    },
    getObservationsColumn(showObservationsModal, 3),
    {
      field: "viewPatient",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View Patient">
          <IconButton size="small" onClick={() => navigate(`/patient-info/${params.row.cipId}`)} sx={{ color: "#6366F1" }}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 170,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onCreateTask(params.row)}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Schedule Reconciliation
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient..."
        filters={[
          { key: "labs", label: "Labs", options: LABS_OPTIONS, value: labsFilter },
          { key: "patientStatus", label: "Patient Status", options: PATIENT_STATUS_OPTIONS, value: patientStatusFilter },
        ]}
        onFilterChange={(key, value) => {
          if (key === "labs") setLabsFilter(value);
          if (key === "patientStatus") setPatientStatusFilter(value);
        }}
      />

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.78rem",
          },
        }}
      />
    </Box>
  );
}
